import { PaymentAllocationMethod } from '@rematter/frontend-lib-api-client';

import type {
  AccountGroup,
  PaymentItem,
} from '../_state/paymentAllocation.state';

export const getSortedItems = <T extends PaymentItem>(
  items: T[],
  method: PaymentAllocationMethod | null,
): T[] =>
  [...items].sort((a, b) => {
    if (!a.dueDate && !b.dueDate) return 0;
    if (!a.dueDate) return 1;
    if (!b.dueDate) return -1;
    if (method === 'LIFO') {
      return b.dueDate.compare(a.dueDate);
    }
    return a.dueDate.compare(b.dueDate);
  });

export const getCheckNumberForGroup = (
  checkNumber: string,
  groupIndex: number,
): string | undefined => {
  if (!checkNumber) return undefined;
  const base = parseInt(checkNumber, 10);
  if (isNaN(base)) return checkNumber;
  const result = base + groupIndex;
  return String(result).padStart(checkNumber.length, '0');
};

interface SubmitPaymentGroupsArgs {
  accountGroups: AccountGroup<PaymentItem>[];
  itemAllocations: Map<string, number>;
  buildMutation: (
    group: AccountGroup<PaymentItem>,
    groupIndex: number,
  ) => Promise<unknown>;
  onAllSuccess: () => void;
  onPartialSuccess: (
    succeededCount: number,
    totalCount: number,
    fulfilledGroups: AccountGroup<PaymentItem>[],
  ) => void;
  onAllFailed: (error: unknown) => void;
}

export async function submitPaymentGroups({
  accountGroups,
  itemAllocations,
  buildMutation,
  onAllSuccess,
  onPartialSuccess,
  onAllFailed,
}: SubmitPaymentGroupsArgs): Promise<void> {
  const submissions = accountGroups
    .map((group, i) => ({ group, index: i }))
    .filter(({ group }) =>
      group.items.some((item) => (itemAllocations.get(item.id) ?? 0) > 0),
    )
    .map(({ group, index }) => ({
      group,
      promise: buildMutation(group, index),
    }));

  const results = await Promise.allSettled(submissions.map((s) => s.promise));
  const failureCount = results.filter((r) => r.status === 'rejected').length;
  const successCount = results.length - failureCount;

  if (failureCount === 0) {
    onAllSuccess();
    return;
  }

  const fulfilledGroups = submissions
    .filter((_, i) => results[i].status === 'fulfilled')
    .map((s) => s.group);

  if (successCount > 0) {
    onPartialSuccess(successCount, results.length, fulfilledGroups);
    return;
  }

  const firstRejected = results.find(
    (r) => r.status === 'rejected',
  ) as PromiseRejectedResult;
  onAllFailed(firstRejected.reason);
}
